import { useEffect } from "react";
import { useMutation } from "@apollo/client";
import { Button, Input } from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { serverGql } from "__generated__/serverGql";
import Form from "components/Shared/Forms/Form";
import createFormFieldComponents from "components/Shared/Forms/FormField";
import useCreateValidSubmitHandler from "components/Shared/Forms/useCreateValidSubmitHandler";
import useAsyncCompleteToast from "components/Shared/useAsyncCompleteToast";
import serverClient from "utils/apollo/serverClient";
import { assertPresence } from "utils/assertions";
import { asyncVoid } from "utils/asyncVoidHandler";
import usePollForForwardedEmailMessages from "utils/usePollForForwardedEmailMessages";

const mutation = serverGql(/* ServerGraphQL */ `
  mutation EmailInboxForwardToAddress(
    $emailId: UUID!
    $email: String!
    $via: String!
  ) {
    forwardReceivedEmailToAddress(
      input: {
        receivedEmailMessageId: $emailId
        emailAddress: $email
        via: $via
      }
    ) {
      lambdaRequestId
    }
  }
`);

type FormFields = {
  email: string;
};

const schema: yup.ObjectSchema<FormFields> = yup.object().shape({
  email: yup
    .string()
    .email()
    .trim()
    .ensure()
    .required()
    .label("Forward to email address"),
});

const { FormField } = createFormFieldComponents<FormFields>({
  schema,
});

type Props = {
  defaultEmail?: string;
  emailId: UUID | null | undefined;
  via?: string;
};

export default function ForwardToEmail({
  defaultEmail = "",
  emailId,
  via = "SnoBase",
}: Props) {
  const { showSuccessToast } = useAsyncCompleteToast();
  const { pendingRequestId, setPendingRequestId } =
    usePollForForwardedEmailMessages({
      onCompleted: () => {
        showSuccessToast("Successfully forwarded email to address");
      },
    });

  const formContext = useForm<FormFields>({
    resolver: yupResolver(schema),
    defaultValues: { email: defaultEmail },
    mode: "onChange",
  });

  useEffect(() => {
    formContext.reset({ email: defaultEmail });
    if (defaultEmail) asyncVoid(formContext.trigger("email"));
  }, [formContext, defaultEmail]);

  const [performUpdate, { loading }] = useMutation(mutation, {
    client: serverClient,
  });

  const [handleValidSubmit] = useCreateValidSubmitHandler(
    (formData: FormFields) => {
      const { email } = formData;
      assertPresence(emailId);
      return performUpdate({ variables: { emailId, email, via } }).then(
        (result) => result.data,
      );
    },
    {
      isSuccessToasted: false,
      onUpdateComplete: (result) => {
        const requestId =
          result?.forwardReceivedEmailToAddress?.lambdaRequestId;
        assertPresence(requestId, "Received unexpected response from request");
        setPendingRequestId(requestId);
      },
    },
  );

  return (
    <Form
      {...{ formContext }}
      isDisabled={!emailId}
      onValidSubmit={handleValidSubmit}
    >
      <FormField
        name="email"
        renderInput={(props) => (
          <Input {...props} type="email" placeholder="Enter email address" />
        )}
        isDisabled={!emailId}
        mt={0}
      />

      <Button
        type="submit"
        colorScheme="pinkButton"
        isLoading={loading || !!pendingRequestId}
        isDisabled={
          !emailId || !formContext.formState.isValid || !!pendingRequestId
        }
        w="100%"
      >
        Forward to Email
      </Button>
    </Form>
  );
}
